import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import { toast } from "sonner";
import { Plus, Calendar as CalIcon } from "lucide-react";
import AppointmentDialog from "./AppointmentDialog";
import AppointmentCard from "./AppointmentCard";
import AppointmentFilters from "./AppointmentFilters";
import TeacherOperationalPanel from "./TeacherOperationalPanel";
import useAppointmentActions from "@/hooks/useAppointmentActions";
import { todayISO } from "@/lib/utils-app";
import { useAuth } from "@/contexts/AuthContext";

function applyFilter(appointments, filter) {
  const today = todayISO();
  return appointments.filter((a) => {
    if (filter === "today") return a.date === today;
    if (filter === "upcoming") return a.date >= today && a.status !== "cancelled";
    if (filter === "pending") return a.status === "pending";
    if (filter === "change-requested") return a.status === "change_requested";
    if (filter === "to-remind")
      return a.date >= today && a.status !== "cancelled" && !a.reminder_sent;
    return true;
  });
}

// V1.38.2 · Exportado para reutilizar la misma lógica de filtrado en
// el toolbar del CalendarTab.
export { applyFilter };

export default function AppointmentsTab() {
  const { user } = useAuth();
  // V1.37-B · Matriz (admin / super_admin) mantiene visibilidad completa.
  // Un usuario Profesor sólo ve la información operativa: se autofiltra por
  // su código (`teacher_code` en el user o inferido) y se ocultan los
  // controles administrativos (crear sesión). Nada se elimina del sistema —
  // sólo se restringe por permisos según la Ley 3 (Natur Academy).
  const role = (user?.role || "").toLowerCase();
  const isMatriz = role === "admin" || role === "super_admin" || role === "matriz" || role === "owner" || user?.is_super_admin === true;
  const teacherCode = user?.teacher_code || "";
  const [appointments, setAppointments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(null); // null | {} (new) | existing
  // V1.37-A fix · La ficha operativa filtra las sesiones al alumno seleccionado
  const [operativeStudentId, setOperativeStudentId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: appts }, { data: studs }] = await Promise.all([
        api.get("/appointments"),
        api.get("/students"),
      ]);
      setAppointments(appts);
      setStudents(studs);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useDataRefresh(load);

  const actions = useAppointmentActions(load);
  const filtered = useMemo(() => {
    const base = applyFilter(appointments, filter);
    // V1.37-B · Profesor: sólo ve sus sesiones. Matriz: ve todo.
    let list = base;
    if (!isMatriz && teacherCode) {
      list = list.filter((a) => (a.teacher || "") === teacherCode);
    }
    // V1.37-A fix · Si hay un alumno seleccionado en la ficha operativa,
    // el listado inferior se acota a ese alumno (incluye titular + compartido).
    if (operativeStudentId) {
      list = list.filter((a) => (
        a.student_id === operativeStudentId ||
        (a.shared_student_ids || []).includes(operativeStudentId) ||
        (a.shared_students || []).some((s) => s?.id === operativeStudentId)
      ));
    }
    return list;
  }, [appointments, filter, isMatriz, teacherCode, operativeStudentId]);

  const handleSaved = useCallback(() => {
    setCreating(null);
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      {/* V1.37 · Ficha operativa del alumno — vista prioritaria para el
          profesor durante clase práctica. Reutiliza el context endpoint. */}
      <TeacherOperationalPanel
        studentId={operativeStudentId}
        onStudentChange={setOperativeStudentId}
      />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <AppointmentFilters value={filter} onChange={setFilter} />
        <div className="flex items-center gap-2">
          {operativeStudentId && (
            <button
              type="button"
              onClick={() => setOperativeStudentId("")}
              data-testid="clear-operative-student-filter"
              className="text-[11px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2.5 py-1 hover:bg-orange-100"
              title="Ver todas las sesiones (quitar filtro por alumno)"
            >
              Filtrado por alumno · quitar
            </button>
          )}
          {isMatriz && (
            <button
              data-testid="create-appointment-button"
              onClick={() => setCreating({})}
              className="btn-primary"
            >
              <Plus className="h-4 w-4" />
              Nueva sesión
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <CalIcon className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
          <div className="font-semibold mb-1">No hay sesiones</div>
          <p className="text-sm text-neutral-500">Crea una sesión para empezar.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => (
            <AppointmentCard
              key={a.id}
              appt={a}
              onWhatsapp={() => actions.sendWhatsapp(a)}
              onReminder={() => actions.sendReminder(a)}
              onConfirm={() => actions.setStatus(a, "confirmed")}
              onReject={(consumes) => actions.setStatus(a, "cancelled", { cancel_consumes: !!consumes })}
              onSetCancelConsumes={(v) => actions.setCancelConsumes(a, v)}
              onToggleAttended={() => actions.toggleAttended(a)}
              onDelete={() => actions.removeAppt(a)}
              onSaveNotes={async (notes) => {
                try { await api.patch(`/appointments/${a.id}`, { notes }); load(); }
                catch { /* silencioso */ }
              }}
              onReload={load}
            />
          ))}
        </div>
      )}

      <AppointmentDialog
        open={creating !== null}
        appointment={creating && creating.id ? creating : null}
        students={students}
        onClose={() => setCreating(null)}
        onSaved={handleSaved}
      />
    </div>
  );
}
